import { Compass, Globe2, ShieldCheck } from "lucide-react";
import CTASection from "../components/sections/common/CTASection";
import FAQAccordion from "../components/sections/common/FAQAccordion";
import PageHeroSplit from "../components/sections/common/PageHeroSplit";
import ProcessSection from "../components/sections/common/ProcessSection";
import PageMeta from "../components/ui/PageMeta";
import Section from "../components/ui/Section";
import SectionHeading from "../components/ui/SectionHeading";
import { ctaPhone, pageCtaContent } from "../data/ctaContent";
import { admissionsPagesContent } from "../data/innerPages";

const content = admissionsPagesContent.mbbsAbroad;

const mbbsAbroadFaqPreview = [
  {
    question: "Can parents be part of MBBS abroad counselling discussions?",
    answer:
      "Yes. Parent participation is encouraged to keep expectations aligned and decisions transparent."
  },
  {
    question: "Do we need to finalize a country before contacting TheCareerSpace?",
    answer:
      "No. Counselling can begin at the evaluation stage when families are still comparing directions."
  },
  {
    question: "Is this support only for shortlisting or for the full admission journey?",
    answer:
      "Support is designed for continuity across major stages, from early evaluation to final decision readiness."
  }
];

const mbbsAbroadProcess = [
  {
    step: "01",
    title: "Understand student goals and readiness",
    description:
      "Counselling begins by understanding profile, aspirations, and family priorities before discussing directions."
  },
  {
    step: "02",
    title: "Evaluate country and college-fit options",
    description:
      "Options are compared with a practical lens so families can move forward with confidence."
  },
  {
    step: "03",
    title: "Guide application preparation flow",
    description:
      "The process is organized into clear stages to reduce confusion during decision-heavy periods."
  },
  {
    step: "04",
    title: "Support final decision confidence",
    description:
      "Final discussions focus on alignment and clarity so families can commit responsibly."
  }
];

const MBBS_ABROAD_HERO_IMAGE =
  "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=1400&q=80";

function MbbsAbroad() {
  return (
    <>
      <PageMeta title={content.meta.title} description={content.meta.description} />

      <PageHeroSplit
        badge={content.hero.badge}
        title={content.hero.title}
        description={content.hero.description}
        highlights={content.hero.highlights}
        primaryCta={{ label: "Start MBBS Abroad Discussion", to: "/contact" }}
        secondaryCta={{ label: "Explore Pathways", to: "/colleges" }}
        sideTitle="Who this supports"
        sideDescription="For students and families seeking structured evaluation before deciding on MBBS abroad."
        sideItems={content.forWho}
        heroImage={MBBS_ABROAD_HERO_IMAGE}
      />

      <Section>
        <SectionHeading
          badge="Guidance Lens"
          title="How TheCareerSpace supports MBBS abroad evaluation"
          description="Support is designed to reduce uncertainty and keep discussions practical, transparent, and family-inclusive."
        />

        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {content.supportBlocks.map((block) => (
            <article key={block.title} className="rounded-2xl border border-brand-line bg-white p-6 shadow-card">
              <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-navy text-white">
                <Compass className="h-4 w-4" />
              </div>
              <h3 className="mt-4 text-xl font-semibold text-brand-navy">{block.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-brand-charcoal/82">{block.description}</p>
            </article>
          ))}
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <article className="rounded-2xl border border-brand-line bg-brand-sky/35 p-5 shadow-card">
            <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-brand-navy">
              <Globe2 className="h-4 w-4" />
            </div>
            <h3 className="mt-3 text-lg font-semibold text-brand-navy">Evaluation-first approach</h3>
            <p className="mt-2 text-sm leading-relaxed text-brand-charcoal/82">
              Counselling prioritizes suitability and informed choices before families move to final commitments.
            </p>
          </article>
          <article className="rounded-2xl border border-brand-line bg-white p-5 shadow-card">
            <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-gold-soft text-brand-navy">
              <ShieldCheck className="h-4 w-4" />
            </div>
            <h3 className="mt-3 text-lg font-semibold text-brand-navy">Reassurance-led communication</h3>
            <p className="mt-2 text-sm leading-relaxed text-brand-charcoal/82">
              Families receive clear next steps and balanced discussions without pressure-driven decisions.
            </p>
          </article>
        </div>
      </Section>

      <ProcessSection
        badge="Journey Support"
        title="How TheCareerSpace structures MBBS abroad guidance"
        description="A steady process that helps students and parents move forward with confidence."
        steps={mbbsAbroadProcess}
      />

      <Section className="bg-white">
        <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
          <SectionHeading
            badge="FAQ Preview"
            title="Common MBBS abroad counselling questions"
            description="Practical clarifications for families beginning international medical planning discussions."
          />
          <FAQAccordion items={mbbsAbroadFaqPreview} />
        </div>
      </Section>

      <CTASection
        {...pageCtaContent.mbbsAbroad}
        phone={ctaPhone}
        secondaryCtaClassName="border-brand-gold/55 bg-brand-gold-soft text-brand-navy hover:border-brand-gold hover:bg-brand-gold hover:text-brand-navy"
      />
    </>
  );
}

export default MbbsAbroad;
